// src/pages/employer/EmployerMatchDetail.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobAdAPI } from '../../services/api';
import { ArrowLeft } from 'lucide-react';

const BAND_COLOURS = {
  EXCELLENT: '#28A745',
  GOOD:      '#3D80CC',
  FAIR:      '#E6A817',
  POOR:      '#CC0000',
};

const ScoreBar = ({ label, score, weight, weightedScore }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{label}</span>
      <span style={{ fontSize: 12, color: '#666' }}>
        {Math.round(score)}/100 x {(weight * 100).toFixed(0)}% = <strong>{weightedScore.toFixed(1)}</strong>
      </span>
    </div>
    <div style={{ height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${Math.min(score, 100)}%`,
        background: score >= 80 ? '#28A745' : score >= 60 ? '#3D80CC' : score >= 40 ? '#E6A817' : '#CC0000',
        borderRadius: 5,
        transition: 'width 0.4s ease',
      }} />
    </div>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.04em' }}>
      {title}
    </div>
    <div style={{ fontSize: 13, color: '#444', lineHeight: 1.7 }}>{children}</div>
  </div>
);

const Chip = ({ children, color = '#555' }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 600, background: `${color}18`, color, marginRight: 4, marginBottom: 4,
  }}>{children}</span>
);

export const EmployerMatchDetail = () => {
  const { matchId } = useParams();
  const { activeOrg, activeProfile } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    jobAdAPI.getCandidateDetail(activeOrg.id, activeProfile.id, matchId)
      .then(({ data }) => setMatch(data.data.match))
      .catch(() => navigate('/employer/candidates', { replace: true }))
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile, matchId]); // eslint-disable-line

  if (loading) return <div className="fade-in" style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;
  if (!match) return null;

  const seeker     = match.jobSeeker || {};
  const ad         = match.adPosition?.jobAd || {};
  const posName    = match.adPosition?.jobDefinition?.name || 'Position';
  const wpName     = match.adPosition?.workplace?.name;
  const breakdown  = match.scoreBreakdown || {};
  const weights    = breakdown.weights || {};

  const scores = [
    { key: 'skills',        label: 'Skills',        score: match.skillScore,        weight: weights.skills || 0 },
    { key: 'experience',    label: 'Experience',     score: match.experienceScore,   weight: weights.experience || 0 },
    { key: 'location',      label: 'Location',       score: match.locationScore,     weight: weights.location || 0 },
    { key: 'education',     label: 'Education',      score: match.educationScore,    weight: weights.education || 0 },
    { key: 'circumstances', label: 'Circumstances',  score: match.circumstanceScore, weight: weights.circumstances || 0 },
    { key: 'language',      label: 'Language',        score: match.languageScore,     weight: weights.language || 0 },
  ];

  const totalWeighted = scores.reduce((sum, s) => sum + s.score * s.weight, 0);

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Match Detail</h1>
      </div>

      <div style={{ paddingBottom: 6 }}>
        <button
          onClick={() => navigate('/employer/candidates')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
            padding: '4px 0', fontFamily: 'inherit',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#3D80CC'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <ArrowLeft size={13} /> All Candidates
        </button>
      </div>

      {/* Header card */}
      <div className="section-box" style={{ marginBottom: 16 }}>
        <div className="section-box-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{seeker.firstName} {seeker.lastName}</span>
          <span style={{
            padding: '3px 14px', borderRadius: 12, fontSize: 14, fontWeight: 700,
            color: '#fff', background: BAND_COLOURS[match.matchBand] || '#999',
          }}>
            {Math.round(match.totalScore)}% — {match.matchBand}
          </span>
        </div>
        <div className="section-box-body" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, color: '#555' }}>
            <div><strong>Job Ad:</strong> {ad.title} {ad.adNumber != null && <span style={{ color: '#999' }}>#{String(ad.adNumber).padStart(4, '0')}</span>}</div>
            <div><strong>Position:</strong> {posName}</div>
            {wpName && <div><strong>Workplace:</strong> {wpName}</div>}
            {seeker.city && <div><strong>Location:</strong> {seeker.city}{seeker.country ? `, ${seeker.country}` : ''}</div>}
          </div>
        </div>
      </div>

      {/* Score breakdown bars */}
      <div className="section-box" style={{ marginBottom: 16 }}>
        <div className="section-box-header">Score Breakdown</div>
        <div className="section-box-body" style={{ padding: '18px 18px 6px' }}>
          {scores.map(s => (
            <ScoreBar
              key={s.key}
              label={s.label}
              score={s.score}
              weight={s.weight}
              weightedScore={s.score * s.weight}
            />
          ))}
          <div style={{ borderTop: '2px solid #ddd', paddingTop: 10, marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>Total Weighted Score</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: BAND_COLOURS[match.matchBand] || '#333' }}>
              {Math.round(totalWeighted * 10) / 10}%
            </span>
          </div>
        </div>
      </div>

      {/* Detailed breakdown per scorer */}
      <div className="section-box">
        <div className="section-box-header">Detailed Analysis</div>
        <div className="section-box-body" style={{ padding: '18px 18px' }}>

          {/* Skills */}
          <DetailSection title="Skills">
            {breakdown.skills ? (
              <>
                {(breakdown.skills.matched || []).length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <strong>Matched: </strong>
                    {breakdown.skills.matched.map(s => <Chip key={typeof s === 'string' ? s : s.name} color="#28A745">{typeof s === 'string' ? s : s.name}</Chip>)}
                  </div>
                )}
                {(breakdown.skills.missing || []).length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <strong>Missing: </strong>
                    {breakdown.skills.missing.map(s => <Chip key={typeof s === 'string' ? s : s.name} color="#CC0000">{typeof s === 'string' ? s : s.name}</Chip>)}
                  </div>
                )}
                {(breakdown.skills.matchedPreferred || []).length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <strong>Preferred matched: </strong>
                    {breakdown.skills.matchedPreferred.map(s => <Chip key={typeof s === 'string' ? s : s.name} color="#3D80CC">{typeof s === 'string' ? s : s.name}</Chip>)}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#888' }}>
                  Required: {breakdown.skills.requiredCount ?? '—'} | Matched: {breakdown.skills.matchedCount ?? (breakdown.skills.matched || []).length}
                </div>
              </>
            ) : <em style={{ color: '#aaa' }}>No skill data</em>}
          </DetailSection>

          {/* Experience */}
          <DetailSection title="Experience">
            {breakdown.experience ? (
              <>
                <div>Candidate years: <strong>{breakdown.experience.candidateYears ?? '—'}</strong> | Required: <strong>{breakdown.experience.requiredYears ?? 'Not specified'}</strong></div>
                {breakdown.experience.candidateLevel && <div>Candidate level: <strong>{breakdown.experience.candidateLevel}</strong> | Required: <strong>{breakdown.experience.requiredLevel || 'Not specified'}</strong></div>}
                {breakdown.experience.yearsReason && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{breakdown.experience.yearsReason}</div>}
                {breakdown.experience.seniorityReason && <div style={{ fontSize: 12, color: '#888' }}>{breakdown.experience.seniorityReason}</div>}
              </>
            ) : <em style={{ color: '#aaa' }}>No experience data</em>}
          </DetailSection>

          {/* Location */}
          <DetailSection title="Location">
            {breakdown.location ? (
              <div>{breakdown.location.reason || `Score: ${match.locationScore}`}</div>
            ) : <em style={{ color: '#aaa' }}>No location data</em>}
          </DetailSection>

          {/* Education */}
          <DetailSection title="Education">
            {breakdown.education ? (
              <>
                <div>Candidate highest: <strong>{breakdown.education.candidateHighest || 'None'}</strong></div>
                {breakdown.education.requiredLevel && <div>Required: <strong>{breakdown.education.requiredLevel}</strong></div>}
                {breakdown.education.baseReason && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{breakdown.education.baseReason}</div>}
                {breakdown.education.fieldBonus > 0 && <div style={{ fontSize: 12, color: '#28A745' }}>Field of study bonus: +{breakdown.education.fieldBonus}</div>}
                {(breakdown.education.matchedFields || []).length > 0 && (
                  <div>Matched fields: {breakdown.education.matchedFields.map(f => <Chip key={f} color="#3D80CC">{f}</Chip>)}</div>
                )}
              </>
            ) : <em style={{ color: '#aaa' }}>No education data</em>}
          </DetailSection>

          {/* Circumstances */}
          <DetailSection title="Circumstances">
            {breakdown.circumstances ? (
              <>
                {breakdown.circumstances.workRights != null && (
                  <div>Work rights: <strong>{seeker.workRightStatus?.replace(/_/g, ' ') || '—'}</strong> (score: {Math.round(breakdown.circumstances.workRights)})</div>
                )}
                {breakdown.circumstances.availability != null && (
                  <div>Availability: <strong>{seeker.availabilityStatus?.replace(/_/g, ' ') || '—'}</strong> (score: {Math.round(breakdown.circumstances.availability)})</div>
                )}
                {breakdown.circumstances.salary != null && (
                  <div>Salary fit: score {Math.round(breakdown.circumstances.salary)}</div>
                )}
              </>
            ) : <em style={{ color: '#aaa' }}>No circumstances data</em>}
          </DetailSection>

          {/* Language */}
          <DetailSection title="Language">
            {breakdown.language ? (
              <>
                {(breakdown.language.matchedRequired || []).length > 0 && (
                  <div>Matched required: {breakdown.language.matchedRequired.map(l => <Chip key={l} color="#28A745">{l}</Chip>)}</div>
                )}
                {(breakdown.language.missingRequired || []).length > 0 && (
                  <div>Missing required: {breakdown.language.missingRequired.map(l => <Chip key={l} color="#CC0000">{l}</Chip>)}</div>
                )}
                {(breakdown.language.matchedPreferred || []).length > 0 && (
                  <div>Matched preferred: {breakdown.language.matchedPreferred.map(l => <Chip key={l} color="#3D80CC">{l}</Chip>)}</div>
                )}
                {!(breakdown.language.matchedRequired?.length || breakdown.language.missingRequired?.length || breakdown.language.matchedPreferred?.length) && (
                  <div>Score: {match.languageScore}</div>
                )}
              </>
            ) : <em style={{ color: '#aaa' }}>No language data</em>}
          </DetailSection>

          {/* Candidate profile summary */}
          <DetailSection title="Candidate Profile Summary">
            {seeker.skills?.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <strong>Skills: </strong>
                {seeker.skills.slice(0, 20).map(s => <Chip key={s.id || s.skill?.name}>{s.skill?.displayName || s.skill?.name}</Chip>)}
                {seeker.skills.length > 20 && <span style={{ fontSize: 11, color: '#888' }}> +{seeker.skills.length - 20} more</span>}
              </div>
            )}
            {seeker.education?.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <strong>Education: </strong>
                {seeker.education.map((e, i) => (
                  <span key={i}>{e.degree}{e.fieldOfStudy ? ` in ${e.fieldOfStudy}` : ''}{e.institution ? ` (${e.institution})` : ''}{i < seeker.education.length - 1 ? '; ' : ''}</span>
                ))}
              </div>
            )}
            {seeker.experience?.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <strong>Experience: </strong>
                {seeker.experience.map((e, i) => (
                  <span key={i}>{e.jobTitle}{e.company ? ` at ${e.company}` : ''}{i < seeker.experience.length - 1 ? '; ' : ''}</span>
                ))}
              </div>
            )}
            {seeker.languages?.length > 0 && (
              <div>
                <strong>Languages: </strong>
                {seeker.languages.map(l => l.name || l.language).filter(Boolean).join(', ')}
              </div>
            )}
          </DetailSection>

        </div>
      </div>
    </div>
  );
};
